import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';

// Redirect /hris/employees/:employeeId → /employees/:employeeId, preserving the param.
function NavigateToEmployee() {
  const { employeeId } = useParams<{ employeeId: string }>();
  return <Navigate to={`/employees/${employeeId ?? ''}`} replace />;
}
import { ProtectedRoute, ALL_ROLES, AdminLayout, ErrorBoundary } from '@/shared/components';
import { LoginPage, PasswordRecoveryPage, PasswordResetPage, UnlockAccountPage } from '@/features/auth';
import { AccountsListPage, AccountDashboardPage } from '@/features/accounts';
import {
  IntelligenceOverviewPage,
  ExecutiveOverviewPage,
  CustomerHealthPage,
  OrganisationDetailPage,
  AlertIntelligencePage,
  DeviceHealthPage,
  CompliancePage,
  OperationsPage,
  CommunicationsOverviewPage,
  EmailAnalyticsPage,
  SmsAnalyticsPage,
  CallsAnalyticsPage,
  AlertSendoutsPage,
  CalibrationCertificatesPage,
  CertificateValidationPage,
  DealInsightsPage,
  ExpansionIntelligencePage,
  BatteryMonitoringPage,
} from '@/features/intelligence/pages';
import { QmsKanbanPage } from '@/features/qms/pages/QmsKanbanPage';
import { QmsSettingsPage } from '@/features/qms/pages/QmsSettingsPage';
import { EqmsFormsPage } from '@/features/qms/pages/EqmsFormsPage';
import { EqmsAuthoringPage } from '@/features/qms/pages/EqmsAuthoringPage';
import { EqmsDashboardPage } from '@/features/qms/pages/EqmsDashboardPage';
import { TrainingPortalPage } from '@/features/qms/pages/TrainingPortalPage';
import { TrainingTaskPage } from '@/features/qms/pages/TrainingTaskPage';
import { ValidationReadinessPage } from '@/features/qms/pages/ValidationReadinessPage';
import { CrmPipelinePage, CrmCompaniesPage, CrmCompanyDetailPage, CrmContactsPage, CrmContactDetailPage, CrmSignalsPage, TenderHunterPage, NewsHunterPage, CrawlersPage, ApolloPage, LinkedInPage, OpportunityDetailPage } from '@/features/crm';
import { EstimateEditorPage, EstimatesListPage, ProposalTemplatesPage } from '@/features/estimates';
import {
  WmsDashboardPage,
  LocationsPage,
  InventoryListPage,
  SerializedItemsPage,
  SerializedItemDetailPage,
  WorkOrdersPage,
  WorkOrderDetailPage,
  PickingListsPage,
  PickingDetailPage,
  PackingSlipsListPage,
  PackingSlipDetailPage,
  ReceivingListPage,
  ReceivingDetailPage,
  SuppliersPage,
  SkusPage,
} from '@/features/wms/pages';
import { WMS_PERMISSIONS } from '@/features/wms/permissions';
import { OS_PERMISSIONS } from '@/features/os/permissions';
import { TRANSMITTER_PERMISSIONS } from '@/features/operations-transmitters/permissions';
import { EquipmentLibraryPage, EQUIPMENT_PERMISSIONS } from '@/features/operations-equipment';
import { QUOTE_PERMISSIONS } from '@/features/quotes/permissions';
import { CustomerOnboardingPage } from '@/features/customer-onboarding';
import { AccountMigrationPage } from '@/features/account-migration';
import { InstallationJobsPage, InstallationJobDetailPage, ChecklistTemplatesPage } from '@/features/installations';
import { InstallationQualificationsPage } from '@/features/installation-qualifications';
import { ProjectsPage } from '@/features/operations-projects';
import { TasksPage } from '@/features/operations-tasks';
import { OperationsDashboardPage, OperationsKanbanPage } from '@/features/operations-dashboard';
import { FinancialPage, QuotesPipelinePage } from '@/features/operations-financial';
import { RevenuePage } from '@/features/operations-revenue';
import { CalendarPage } from '@/features/operations-calendar';
import { ChangelogPage } from '@/features/changelog';
import { SimInventoryPage } from '@/features/sim-management';
import { InactiveSensorsPage } from '@/features/operations-inactive-sensors';
import { SensorsPage } from '@/features/operations-sensors';
import { TransmittersPage } from '@/features/operations-transmitters';
import { GatewaysPage } from '@/features/operations-gateways';
import { MeshPage } from '@/features/operations-mesh';
import { NetworkTopologyPage } from '@/features/network-topology';
import { CalibratorsPage } from '@/features/calibrators';
import { CalibrationProceduresPage } from '@/features/calibration-procedures';
import { LoRaCalibrationPage } from '@/features/operations-lora-calibration';
import { CompanyPulsePage, CompanyPulseLayout, PULSE_ROLES } from '@/features/company-pulse/CompanyPulsePage';
import { KpiScorecardPage } from '@/features/kpis';
import { HeadlinesPage } from '@/features/headlines';
import { RocksPage } from '@/features/rocks';
import { VtoPage } from '@/features/vto';
import { OsTodosPage } from '@/features/os-todos';
import { PublicationsPage } from '@/features/publications';
import { BrandPage } from '@/features/brand';
import { TeamsPage } from '@/features/teams';
import { OperationsSettingsPage } from '@/features/operations-settings';
import { CloudSpendPage } from '@/features/cloud-spend';
import { AllUsersPage } from '@/features/users';
import { RolesPage } from '@/features/roles';
import { InvoicesPage } from '@/features/invoices';
import { StatementsPage, StatementDetailPage } from '@/features/statements';
import { PnLDashboard, GLDetail } from '@/features/pnl';
import { BalanceSheetDashboard } from '@/features/balance-sheet';
import { QboInvoicesPage } from '@/features/qbo-invoices';
import { AccountsReceivablePage, AccountsPayablePage, ArBackfillReportPage } from '@/features/aging';
import { BillsListPage } from '@/features/bills';
import { ACCOUNTING_PERMISSIONS } from '@/features/accounting/permissions';
import { AccDashboardPage } from '@/features/acc-dashboard';
import { SitesPage } from '@/features/sites';
import { QuotesPage } from '@/features/quotes';
import { OrdersPage } from '@/features/orders';
import { SubscriptionsPage } from '@/features/subscriptions';
import { LocationsPage as ErpLocationsPage } from '@/features/locations';
import { ShippingsPage } from '@/features/shippings';
import { CanadaPostPage } from '@/features/canada-post';
import { DhlPage } from '@/features/dhl';
import { PlaneteCourrierPage } from '@/features/planete-courrier';
import { PLANETE_COURRIER_PAGE_ROLES } from '@/features/planete-courrier/permissions';
import { CarmPage } from '@/features/carm';
import { RmaListPage, RmaDetailPage } from '@/features/rma';
import { PacketInterpreterPage, PacketInterpreterDraginoPage } from '@/features/packet-interpreter';
import { ApiKeysPage } from '@/features/api-keys';
import { ScheduledJobsPage } from '@/features/scheduled-jobs';
import { MaintenanceEventsPage } from '@/features/maintenance-events';
import { StreamsPage } from '@/features/streams';
import { AgentsSettingsPage, AgentsListPage, ToolsListPage, AgentChatPage, WorkflowListPage, WorkflowDetailPage, MastraPage } from '@/features/agents';
import { ForecastDashboardPage } from '@/features/forecasts';
import { DocumentsPage, ClassificationPage, TagManagementPage, DocumentsSettingsPage, DuplicatesPage } from '@/features/documents';
import { WikiAssistantPage } from '@/features/wiki-assistant';
import { HrisEmployeesPage } from '@/features/hris/pages/HrisEmployeesPage';
import { HrisEmployeeDetailPage } from '@/features/hris/pages/HrisEmployeeDetailPage';
import { HrisRolesPage } from '@/features/hris/pages/HrisRolesPage';
import { HrisSeatsPage } from '@/features/hris/pages/HrisSeatsPage';
import { HrisOrgChartPage } from '@/features/hris/pages/HrisOrgChartPage';
import { HrisJobsPage } from '@/features/hris/pages/HrisJobsPage';
import { JobEditor } from '@/features/hris/pages/JobEditor';
import { HrisRecruitmentPage } from '@/features/hris/pages/HrisRecruitmentPage';
import { ProcessesPage, ProcessGraphPage } from '@/features/processes';
import { MeetingsPage, MeetingRunPage } from '@/features/meetings';
import { AmDashboardPage } from '@/features/am-dashboard';
import { CustomerRequestsPage, CUSTOMER_REQUEST_VIEWER_ROLES } from '@/features/customer-requests';
import { AccountabilityPage } from '@/features/accountability';
import { SeatDetailPage } from '@/features/accountability/pages/SeatDetailPage';
import { TransmitterRawEventsPage } from '@/features/transmitter-raw-events';
import { PostgresProfilerPage } from '@/features/postgres-profiler';
import { CalibrationPlanningPage } from '@/features/operations-calibration-planning';

function NotFoundPage() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-2">404</h1>
        <p className="text-muted-foreground mb-4">Page not found</p>
        <a href="/accounts" className="text-primary hover:underline">
          Go to Accounts
        </a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public routes (no auth required) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/password-recovery" element={<PasswordRecoveryPage />} />
          <Route path="/password-reset/:token" element={<PasswordResetPage />} />
          <Route path="/unlock-account" element={<UnlockAccountPage />} />

          {/* Company-wide publication has its own employee gate, including CSM-only users. */}
          <Route element={<ProtectedRoute requiredRoles={PULSE_ROLES}><CompanyPulseLayout /></ProtectedRoute>}>
            <Route path="/company-pulse" element={<CompanyPulsePage />} />
          </Route>

          {/* Authenticated tools that do not require a PostgreSQL role */}
          <Route
            element={
              <ProtectedRoute requiredRoles={[]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/tools/transmitter-raw-events" element={<TransmitterRawEventsPage />} />
          </Route>

          {/* Protected admin routes with layout - requires any PostgreSQL role */}
          <Route
            element={
              <ProtectedRoute requiredRoles={ALL_ROLES}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            {/* Redirect root to accounts */}
            <Route index element={<Navigate to="/accounts" replace />} />

            {/* Account routes */}
            <Route path="/accounts" element={<AccountsListPage />} />
            <Route path="/accounts/:accountId" element={<AccountDashboardPage />} />

            {/* AM Dashboard */}
            <Route path="/am-dashboard" element={
              <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager', 'csm']}>
                <AmDashboardPage />
              </ProtectedRoute>
            } />

            {/* Customer requests (spec: docs/superpowers/specs/2026-10-21-customer-requests-design.md) */}
            <Route path="/customer-requests" element={
              <ProtectedRoute requiredRoles={CUSTOMER_REQUEST_VIEWER_ROLES}>
                <CustomerRequestsPage />
              </ProtectedRoute>
            } />

            {/* Intelligence routes */}
            <Route path="/intelligence" element={<IntelligenceOverviewPage />} />
            <Route path="/intelligence/executive" element={<ExecutiveOverviewPage />} />
            <Route path="/intelligence/customers" element={<CustomerHealthPage />} />
            <Route path="/intelligence/customers/:customerId" element={<OrganisationDetailPage />} />
            <Route path="/intelligence/alerts" element={<AlertIntelligencePage />} />
            <Route path="/intelligence/devices" element={<DeviceHealthPage />} />
            <Route path="/intelligence/compliance" element={<CompliancePage />} />
            <Route path="/intelligence/operations" element={<OperationsPage />} />
            <Route path="/intelligence/communications" element={<CommunicationsOverviewPage />} />
            <Route path="/intelligence/communications/email" element={<EmailAnalyticsPage />} />
            <Route path="/intelligence/communications/sms" element={<SmsAnalyticsPage />} />
            <Route path="/intelligence/communications/calls" element={<CallsAnalyticsPage />} />
            <Route path="/intelligence/communications/alerts" element={<AlertSendoutsPage />} />
            <Route
              path="/intelligence/calibration-certificates"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['calibrationrecords.read']}>
                  <CalibrationCertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route path="/intelligence/deals" element={<DealInsightsPage />} />
            <Route path="/intelligence/expansion" element={<ExpansionIntelligencePage />} />
            <Route path="/intelligence/battery" element={<BatteryMonitoringPage />} />
            <Route path="/intelligence/forecasts" element={
              <ProtectedRoute requiredRoles={['full_admin', 'director', 'operations_manager', 'sales_manager']}>
                <ForecastDashboardPage />
              </ProtectedRoute>
            } />

            {/* QMS routes. Visibility is still team-scoped by the backend. */}
            <Route
              path="/eqms"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'quality_manager']}>
                  <EqmsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/eqms/authoring/:draftId" element={<ProtectedRoute requiredRoles={['full_admin', 'director', 'quality_manager']}><EqmsAuthoringPage /></ProtectedRoute>} />
            <Route path="/eqms/forms" element={<ProtectedRoute requiredRoles={[]}><EqmsFormsPage /></ProtectedRoute>} />
            <Route path="/eqms/training" element={<TrainingPortalPage />} />
            <Route path="/eqms/training/:assignmentId" element={<TrainingTaskPage />} />
            <Route
              path="/eqms/validation"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'quality_manager']}>
                  <ValidationReadinessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qms"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['qmstickets.read']}>
                  <QmsKanbanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qms/:ticketId"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['qmstickets.read']}>
                  <QmsKanbanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qms/settings"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <QmsSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* CRM routes - full_admin, director, and sales_manager */}
            <Route
              path="/crm/pipeline"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager']}>
                  <CrmPipelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/opportunities/:opportunityId"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager']}>
                  <OpportunityDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/companies/:companyId"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager', 'operations_manager']}>
                  <CrmCompanyDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/companies"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager', 'operations_manager']}>
                  <CrmCompaniesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/contacts/:contactId"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager', 'operations_manager']}>
                  <CrmContactDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/contacts"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager', 'operations_manager']}>
                  <CrmContactsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/signals"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <CrmSignalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/signals/tender-hunter"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <TenderHunterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/signals/news-hunter"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.news.access']}>
                  <NewsHunterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/signals/crawlers"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.webcrawler.access']}>
                  <CrawlersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/apollo"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.apollo.access']}>
                  <ApolloPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/linkedin"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.linkedin.access']}>
                  <LinkedInPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/estimates"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager']}>
                  <EstimatesListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/estimates/:estimateId"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'sales_manager']}>
                  <EstimateEditorPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/settings/proposal-templates"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <ProposalTemplatesPage />
                </ProtectedRoute>
              }
            />

            {/* Marketing routes - permission gated */}
            <Route
              path="/marketing/publication"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.publication.access']}>
                  <PublicationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing/brand"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.brand.access']}>
                  <BrandPage />
                </ProtectedRoute>
              }
            />

            {/* WMS routes - permission gated */}
            <Route
              path="/wms"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.dashboard.access]}>
                  <WmsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/locations"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.locations.read]}>
                  <LocationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/inventory"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.inventory.read]}>
                  <InventoryListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/serials"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.serials.read]}>
                  <SerializedItemsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/serials/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.serials.read]}>
                  <SerializedItemDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/work-orders"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.workOrders.read]}>
                  <WorkOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/work-orders/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.workOrders.read]}>
                  <WorkOrderDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/picking"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.picking.read]}>
                  <PickingListsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/picking/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.picking.read]}>
                  <PickingDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/shipping"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.shipping.read]}>
                  <PackingSlipsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/shipping/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.shipping.read]}>
                  <PackingSlipDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.procurement.read]}>
                  <ReceivingListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.procurement.read]}>
                  <ReceivingDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wms/suppliers"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.suppliers.read]}>
                  <SuppliersPage view="wms" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/suppliers"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.suppliers.read]}>
                  <SuppliersPage view="accounting" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/skus"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.skus.read]}>
                  <SkusPage />
                </ProtectedRoute>
              }
            />
            {/* Backward-compat: keep /wms/skus working */}
            <Route
              path="/wms/skus"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[WMS_PERMISSIONS.skus.read]}>
                  <SkusPage />
                </ProtectedRoute>
              }
            />

            {/* Resources routes */}
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/documents" element={
              <ProtectedRoute requiredPermissions={['documents.access']}>
                <DocumentsPage />
              </ProtectedRoute>
            } />
            <Route path="/documents/classification" element={
              <ProtectedRoute requiredRoles={['full_admin']}>
                <ClassificationPage />
              </ProtectedRoute>
            } />
            <Route path="/documents/tags" element={
              <ProtectedRoute requiredRoles={['full_admin']}>
                <TagManagementPage />
              </ProtectedRoute>
            } />
            <Route path="/documents/duplicates" element={
              <ProtectedRoute requiredRoles={['full_admin']}>
                <DuplicatesPage />
              </ProtectedRoute>
            } />
            <Route path="/documents/settings" element={
              <ProtectedRoute requiredRoles={['full_admin']}>
                <DocumentsSettingsPage />
              </ProtectedRoute>
            } />

            {/* Operations routes - accessible to all roles (settings and sim-cards are full_admin/director only) */}
            <Route
              path="/operations/settings"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <OperationsSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/cloud-spend"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <CloudSpendPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/customer-onboarding"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <CustomerOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/account-migration"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <AccountMigrationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/installations"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <InstallationJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/installations/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <InstallationJobDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/checklist-templates"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <ChecklistTemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/validation/installation-qualifications"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <InstallationQualificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/equipment"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[EQUIPMENT_PERMISSIONS.read]}>
                  <EquipmentLibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/dashboard"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <OperationsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/dashboard/kanban"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <OperationsKanbanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/projects"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/tasks"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <TasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/financial"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <FinancialPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/quotes-pipeline"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <QuotesPipelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/revenue"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <RevenuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/calendar"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/sim-cards"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <SimInventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/sensors"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <SensorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/inactive-sensors"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <InactiveSensorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/transmitters"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[TRANSMITTER_PERMISSIONS.read]}>
                  <TransmittersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/gateways"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <GatewaysPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/mesh"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <MeshPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/network-topology"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <NetworkTopologyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/streams"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <StreamsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/calibrators"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'operations_manager']}>
                  <CalibratorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/calibration-procedures"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director', 'operations_manager']}>
                  <CalibrationProceduresPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/calibration/certificates"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['calibrationrecords.read']}>
                  <CalibrationCertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/calibration/validation"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['calibrationrecords.read']}>
                  <CertificateValidationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/calibration/planning"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['calibrationrecords.read']}>
                  <CalibrationPlanningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/lora-calibration"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <LoRaCalibrationPage />
                </ProtectedRoute>
              }
            />

            {/* RMA routes */}
            <Route
              path="/operations/rma"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'operations_manager', 'director']}>
                  <RmaListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/rma/:id"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'operations_manager', 'director']}>
                  <RmaDetailPage />
                </ProtectedRoute>
              }
            />

            {/* KPI routes */}
            <Route
              path="/kpis"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.kpis.read]}>
                  <KpiScorecardPage />
                </ProtectedRoute>
              }
            />

            {/* Headlines route */}
            <Route
              path="/headlines"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.headlines.read]}>
                  <HeadlinesPage />
                </ProtectedRoute>
              }
            />

            {/* OS - EOS Operating System */}
            <Route
              path="/rocks"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.rocks.read]}>
                  <RocksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/todos"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.todos.read]}>
                  <OsTodosPage />
                </ProtectedRoute>
              }
            />
            <Route path="/os/todos" element={<Navigate to="/todos" replace />} />
            <Route path="/os/meetings" element={
              <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.meetings.read]}>
                <MeetingsPage />
              </ProtectedRoute>
            } />
            <Route path="/os/meetings/:id" element={
              <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.meetings.read]}>
                <MeetingRunPage />
              </ProtectedRoute>
            } />
            <Route path="/vto" element={
              <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[OS_PERMISSIONS.vto.read]}>
                <VtoPage />
              </ProtectedRoute>
            } />
            <Route path="/os/vto" element={<Navigate to="/vto" replace />} />
            <Route path="/seats" element={
              <ProtectedRoute requiredRoles={ALL_ROLES}>
                <HrisSeatsPage />
              </ProtectedRoute>
            } />
            <Route path="/seats/:seatId" element={
              <ProtectedRoute requiredRoles={ALL_ROLES}>
                <SeatDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/org-chart" element={
              <ProtectedRoute requiredRoles={ALL_ROLES}>
                <HrisOrgChartPage />
              </ProtectedRoute>
            } />
            {/* Legacy accountability redirects */}
            <Route path="/os/accountability" element={
              <ProtectedRoute requiredRoles={ALL_ROLES}>
                <AccountabilityPage />
              </ProtectedRoute>
            } />
            <Route path="/os/accountability/seats/:seatId" element={
              <Navigate to="/seats" replace />
            } />
            <Route
              path="/team"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <TeamsPage />
                </ProtectedRoute>
              }
            />
            {/* Legacy redirects — preserve old bookmarks */}
            <Route path="/os/team" element={<Navigate to="/team" replace />} />
            <Route path="/os/settings/teams" element={<Navigate to="/team" replace />} />

            {/* HR / Employees */}
            <Route
              path="/employees"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <HrisEmployeesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/:employeeId"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <HrisEmployeeDetailPage />
                </ProtectedRoute>
              }
            />
            <Route path="/hris/employees" element={<Navigate to="/employees" replace />} />
            <Route path="/hris/employees/:employeeId" element={<NavigateToEmployee />} />

            {/* HR / Job postings */}
            <Route
              path="/jobs"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <HrisJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/new"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <JobEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:id/edit"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <JobEditor />
                </ProtectedRoute>
              }
            />

            {/* HR / Recruitment kanban */}
            <Route
              path="/recruitment"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <HrisRecruitmentPage />
                </ProtectedRoute>
              }
            />

            {/* Processes (process maps with React Flow) — under HRIS */}
            <Route
              path="/processes"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <ProcessesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/processes/:processId"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <ProcessGraphPage />
                </ProtectedRoute>
              }
            />

            {/* HR / Role documentation */}
            <Route
              path="/roles-doc"
              element={
                <ProtectedRoute requiredRoles={['full_admin', 'director']}>
                  <HrisRolesPage />
                </ProtectedRoute>
              }
            />

            {/* ERP routes - accessible to all roles */}
            <Route
              path="/erp/users"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <AllUsersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/erp/roles"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <RolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp/invoices"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['invoices.read']}>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/dashboard"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.pnl.read]}>
                  <AccDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/statements"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.reconciliations.read]}>
                  <StatementsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/statements/:id"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.reconciliations.read]}>
                  <StatementDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/pnl"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.pnl.read]}>
                  <PnLDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/balance-sheet"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.pnl.read]}>
                  <ErrorBoundary>
                    <BalanceSheetDashboard />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/qbo-pnl/gl/:accountId"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.pnl.read]}>
                  <GLDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/invoices"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.invoices.read]}>
                  <QboInvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/invoices/:invoiceNumber"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.invoices.read]}>
                  <QboInvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/ar"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.receivable.read]}>
                  <AccountsReceivablePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/ar/backfill-report"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.receivable.read]}>
                  <ArBackfillReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/ap"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.payable.read]}>
                  <AccountsPayablePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acc/bills"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[ACCOUNTING_PERMISSIONS.bills.read]}>
                  <BillsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp/sites"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <SitesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp/quotes"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={[QUOTE_PERMISSIONS.read]}>
                  <QuotesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp/orders"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['orders.read']}>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts/subscriptions"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['subscriptions.read']}>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp/locations"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <ErpLocationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp/shippings"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <ShippingsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tools/shipping/planete-courrier"
              element={
                <ProtectedRoute requiredRoles={PLANETE_COURRIER_PAGE_ROLES}>
                  <PlaneteCourrierPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/shipping/canada-post"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <CanadaPostPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/shipping/dhl"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <DhlPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tools/telnyx"
              element={<Navigate to="/operations/sim-cards?provider=telnyx" replace />}
            />

            <Route
              path="/erp/carm"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <CarmPage />
                </ProtectedRoute>
              }
            />

            {/* Tools routes - accessible to all roles */}
            <Route
              path="/tools/packet-interpreter"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <PacketInterpreterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/packet-interpreter/dragino-lht65"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <PacketInterpreterDraginoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/postgres-profiler"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <PostgresProfilerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/api-keys"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <ApiKeysPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/scheduled-jobs"
              element={
                <ProtectedRoute requiredRoles={['full_admin']}>
                  <ScheduledJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/maintenance-events"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES} requiredPermissions={['marketing.maintenance_events.access']}>
                  <MaintenanceEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wiki-assistant"
              element={
                <ProtectedRoute requiredRoles={ALL_ROLES}>
                  <WikiAssistantPage />
                </ProtectedRoute>
              }
            />

            {/* AI routes */}
            <Route path="/ai/agents" element={<AgentsListPage />} />
            <Route path="/ai/tools" element={<ToolsListPage />} />
            <Route path="/ai/chat/:agentId" element={<AgentChatPage />} />
            <Route path="/ai/workflows" element={<WorkflowListPage />} />
            <Route path="/ai/workflows/:id" element={<WorkflowDetailPage />} />
            <Route path="/ai/settings" element={<AgentsSettingsPage />} />
            <Route path="/ai/mastra" element={<MastraPage />} />

            {/* 404 within admin */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
